import { useCallback } from 'react'
import type { RouterOutputs } from '@/lib/trpc'
import { trpc } from '../../../../lib/trpc.js'
import { useResource } from './use-resource'

export interface TemplateCodeItem {
  language: string
  definitionCode: string
  helperCode?: string
  testCode: string
  testExpectedOutput: string
  lastValidatedAt: string | null
}

export interface TestTemplate {
  id: string
  name: string
  slug: string
  description: string | null
  order: number
  createdAt: string
  updatedAt: string
  code: TemplateCodeItem[]
  languageSummary: {
    python?: boolean
    javascript?: boolean
  }
}

export interface TemplateFormData {
  name: string
  description?: string | null
  code?: Array<{
    language: string
    definitionCode: string
    helperCode?: string
    testCode: string
    testExpectedOutput: string
  }>
}

export interface ValidationResult {
  success: boolean
  actual: string
  expected: string
  error?: string
  stderr?: string
}

// The router types `data` more loosely than the local TestTemplate interface;
// callers only ever read the fields TestTemplate declares.
const selectTemplates = (
  data: RouterOutputs['admin']['testTemplates']['list'] | undefined,
): TestTemplate[] => (data?.data as TestTemplate[]) ?? []

export function useTestTemplates() {
  const utils = trpc.useUtils()

  const invalidate = useCallback(() => {
    utils.admin.testTemplates.list.invalidate()
  }, [utils])

  const {
    items: templates,
    loading,
    error,
    refresh,
    create,
    update,
    remove,
  } = useResource({
    query: trpc.admin.testTemplates.list.useQuery(),
    select: selectTemplates,
    invalidate,
    create: trpc.admin.testTemplates.create,
    update: trpc.admin.testTemplates.update,
    remove: trpc.admin.testTemplates.delete,
  })

  const validateMutation = trpc.admin.testTemplates.validate.useMutation({
    onSuccess: (result) => {
      if (result.success) invalidate()
    },
  })

  const createTemplate = async (
    data: TemplateFormData,
  ): Promise<TestTemplate> => {
    const { description, ...rest } = data
    return (await create({
      ...rest,
      description: description ?? undefined,
    })) as TestTemplate
  }

  const updateTemplate = async (
    id: string,
    data: Partial<TemplateFormData>,
  ): Promise<TestTemplate> => {
    const { description, ...rest } = data
    return (await update({
      id,
      ...rest,
      description: description ?? undefined,
    })) as TestTemplate
  }

  const deleteTemplate = async (id: string): Promise<void> => {
    await remove({ id })
  }

  const getTemplate = async (id: string): Promise<TestTemplate> => {
    return (await utils.admin.testTemplates.getById.fetch({
      id,
    })) as TestTemplate
  }

  const validateTemplate = async (
    id: string,
    language: string,
    options?: {
      definitionCode?: string
      helperCode?: string
      testCode?: string
      testExpectedOutput?: string
    },
  ): Promise<ValidationResult> => {
    return (await validateMutation.mutateAsync({
      id,
      language: language as 'python' | 'javascript',
      ...options,
    })) as ValidationResult
  }

  return {
    templates,
    loading,
    error,
    refresh,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplate,
    validateTemplate,
  }
}
